import { GovtBuses, SelectedFilters } from "src/context/resultsContext/types";
import dayjs from "dayjs";
import { Bus, Categories, DepartureTime, FilterList } from "./types";
import { formatFare, generateUniqueArray } from "src/utils/generalUtils";
import { getAmenities } from "src/utils/amenities";
import { isEmpty, isWindowPresent } from "src/utils/browserHelpers";
import { Rs } from "constants/common";
// import { getAmenities } from "src/icons/icon";
/**
 * @abstract: function to sort result data
 * @param type: type of sorting
 */

export const handleSorting = (type: string) => {
  switch (type) {
    case "cheapest":
      return (a: Bus, b: Bus) => {
        if (a.fares[0].total < b.fares[0].total) {
          return -1;
        }
        if (a.fares[0].total > b.fares[0].total) {
          return 1;
        }
        return 0;
      };
    case "top_rated":
      return (a: Bus, b: Bus) => {
        if ((a.review?.rating || 0) < (b.review?.rating || 0)) {
          return 1;
        }
        if ((a.review?.rating || 0) > (b.review?.rating || 0)) {
          return -1;
        }
        return 0;
      };
    case "late_departure":
      return (a: Bus, b: Bus) => {
        if (dayjs(a.deptTime).diff(dayjs(b.deptTime)) > 0) {
          return -1;
        }
        if (dayjs(a.deptTime).diff(dayjs(b.deptTime)) < 0) {
          return 1;
        }
        return 0;
      };
    case "early_departure":
      return (a: Bus, b: Bus) => {
        if (a.deptTime < b.deptTime) {
          return -1;
        }
        if (a.deptTime > b.deptTime) {
          return 1;
        }
        return 0;
      };
  }
};

/**
 * @abstract Fucntion to get an list of selected filters array
 * @param filterSelection: list of filters with updated selected value
 */

export const getSelectedFilters = (
  filterSelection: Record<string, FilterList[] | string[] | number[]>
) => {
  const selected: {
    [key: string]: (string | DepartureTime | number)[];
  } = {};
  const keys = Object.keys(filterSelection);
  keys.forEach((key) => {
    selected[key] = (filterSelection[key] as FilterList[])
      .filter((item) => {
        if (item.selected) return item.value;
      })
      .map((item) => item.value);
  });

  // creating a new seatType object for sleeper, seater to perform and operation while filtering
  selected["seatType"] = selected["busType"].filter(
    (item) => item === "isSeater" || item === "isSleeper"
  );

  if (selected["seatType"].length) {
    // removing seat types from busType array
    selected["seatType"].forEach((item) => {
      const index = selected["busType"].indexOf(item);
      selected["busType"].splice(index, 1);
    });
  }

  return selected;
};

export const getCheapestFare = (busList: Bus[]) => {
  return busList.sort(handleSorting("cheapest"))[0]?.fares[0].total;
};

export const getCheapestGovtFare = (busArray: GovtBuses) => {
  let lowest = Number.POSITIVE_INFINITY;
  const val = Object.keys(busArray).map((item) => {
    let tmp = 0;
    for (let i = busArray[item].length - 1; i >= 0; i--) {
      tmp = busArray[item][i].fares[0].total;
      if (tmp < lowest) lowest = tmp;
    }
    return tmp;
  });

  return Math.min(...val);
};

export const getTotalRTCBuses = (busArray: GovtBuses) => {
  let length = 0;
  Object.keys(busArray).forEach((item) => {
    length += busArray[item].length;
  });

  return length;
};

export const checkNoResults = (data: any) => {
  const normalBusExist = !!data?.newFilterResults?.buses?.length;
  if (!isEmpty(data?.newFilteredGovtBuses)) {
    const filteredGovtBusExist = Object.keys(data.newFilteredGovtBuses).some(
      (key) => data.newFilteredGovtBuses[key]?.length
    );
    return normalBusExist || filteredGovtBusExist ? false : true;
  } else return normalBusExist ? false : true;
};

export const getGovtBusesList = (buses: Bus[], categories: Categories[]) => {
  let govtBusesList: GovtBuses = {};

  buses.forEach((bus) => {
    categories.some((element) => {
      if (element.companies.includes(bus.meta.operatorId)) {
        if (govtBusesList[element.label]?.length) {
          govtBusesList[element.label].push(bus);
        } else {
          govtBusesList = {
            ...govtBusesList,
            [element.label]: [bus],
          };
        }
      }
    });
  });
  return govtBusesList;
};

export const getNormalBuses = (buses: Bus[], categories: Categories[]) => {
  const normalBuses = buses.filter((bus) =>
    categories.every(
      (element) => !element.companies.includes(bus.meta.operatorId)
    )
  );
  return normalBuses;
};

const getMappedArray = (
  data: string[],
  type = "",
  selectedFilters: SelectedFilters
) => {
  return data.map((item, index) => {
    const titleWithoutSplChars = item.replace(/\.$/, "  ");

    return {
      id: index + 1,
      title: titleWithoutSplChars,
      selected: selectedFilters?.[type]?.includes(item) ? true : false,
      value: item,
      ...(type === "amenities" && { icon: getAmenities(item as string) }),
    };
  });
};

export const generatListForFilters = (
  busData: Bus[],
  selectedFilters: SelectedFilters
) => {
  let amenities: FilterList[] | string[] = [];
  let pickupPoints: FilterList[] | string[] = [];
  let dropOffPoints: FilterList[] | string[] = [];
  let busOperators: FilterList[] | string[] = [];

  busData.map((item: Bus) => {
    amenities = generateUniqueArray(item.meta.amenities, amenities) as string[];
    pickupPoints = generateUniqueArray(item.pickups, pickupPoints) as string[];

    dropOffPoints = generateUniqueArray(
      item.dropOffs,
      dropOffPoints
    ) as string[];

    if (!(busOperators as string[]).includes(item.meta.operatorName)) {
      (busOperators as string[]).push(item.meta.operatorName);
    }
  });
  amenities = getMappedArray(
    amenities as string[],
    "amenities",
    selectedFilters
  ) as FilterList[];
  pickupPoints = getMappedArray(
    pickupPoints as string[],
    "pickupPoints",
    selectedFilters
  ) as FilterList[];
  dropOffPoints = getMappedArray(
    dropOffPoints as string[],
    "dropOffPoints",
    selectedFilters
  ) as FilterList[];
  busOperators = getMappedArray(
    busOperators as string[],
    "busOperators",
    selectedFilters
  ) as FilterList[];
  return {
    amenities,
    pickupPoints,
    dropOffPoints,
    busOperators,
  };
};

//check if we have filters in session storage fro retention
export const isFiltersPresentInSS = () => {
  const selectedFiltersFromSS = isWindowPresent
    ? sessionStorage.getItem("selectedFilters") !== null &&
      JSON.parse(sessionStorage.getItem("selectedFilters") as string)
    : null;

  let filterPresentInSS = false;

  if (selectedFiltersFromSS) {
    filterPresentInSS = Object.keys(selectedFiltersFromSS).some(
      (filterType) => filterType.length > 0
    );
    return filterPresentInSS;
  } else return false;
};

export const getMessageFromObject = (
  message: string,
  object: Record<string, string | number>
) => {
  return (
    object &&
    message?.replace(
      /\{(\w+)\}/g,
      (match: string, varName: string | number) => {
        if (typeof object[varName] === "number")
          return `${Rs}${formatFare(object[varName] || match)}`;
        else return String(object[varName] || match);
      }
    )
  );
};
